import React, { JSX, useEffect, useState } from "react";
import { ReactNode } from "react";
import { Instruction, Recipe, State } from "../state";
import {
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Collapse,
  IconButton,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { updateRecipe, writeRecipe } from "../../api";
import { SearchBox } from "./RecipeBookModal";
import { localStorageRecipeKey } from "../../App";

export interface RecipeEditorProps {
  setState: React.Dispatch<React.SetStateAction<State>>;
  state: State;
}

export interface TextInputProps {
  placeholder: string;
  title: string;
  multiline?: boolean;
  required?: boolean;
  expandible?: boolean;
  hide?: boolean;
}

export interface InstructionInput extends TextInputProps {
  instruction: keyof Instruction | "shoppingListIngredients";
}

export interface CollapseWrapperProps {
  content: JSX.Element;
  title: string;
  hide?: boolean;
}
export function CollapseWrapper(props: CollapseWrapperProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton
        onClick={() => setOpen(!open)}
        aria-label="expand"
        size="small"
      >
        {props.title}
        {open ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
      </IconButton>
      <Collapse in={open} timeout="auto" mountOnEnter unmountOnExit>
        {props.content}
      </Collapse>
    </>
  );
}

export interface ContentWrapperProps extends CollapseWrapperProps {
  expandible?: boolean;
}
export function ContentWrapper(props: ContentWrapperProps) {
  return (
    <Box
      sx={{
        display: props.hide ? "none" : "flex",
        alignItems: "center",
        background: "linear-gradient(135deg, #e0e7ef 0%, #d6dde5 100%)",
        p: 2,
        justifyContent: "center",
        flexDirection: "column",
        placeItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          p: 4,
          borderRadius: 4,
          background: `linear-gradient(
            to bottom right,
            rgba(255, 255, 255, 0.25),
            rgba(255, 255, 255, 0.05)
          )`,
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          border: "1px solid rgba(255, 255, 255, 0.18)",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
        }}
      >
        {props.expandible
          ? CollapseWrapper({
              ...props,
            })
          : props.content}
      </Box>
    </Box>
  );
}

export function TextInput(props: TextInputProps) {
  const [value, setValue] = useState("");

  return ContentWrapper({
    ...props,
    content: (
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, color: "#2b2b2b", fontWeight: 500 }}
        >
          {props.title}
        </Typography>

        <TextField
          placeholder={props.placeholder}
          multiline={props.multiline ?? false}
          minRows={props.multiline ? 4 : undefined}
          fullWidth
          variant="filled"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{
            textarea: {
              color: "#333",
            },
            ".MuiFilledInput-root": {
              backgroundColor: "rgba(255, 255, 255, 0.25)",
              borderRadius: 2,
              color: "#333",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.35)",
              },
              "&.Mui-focused": {
                backgroundColor: "rgba(255, 255, 255, 0.4)",
              },
            },
          }}
        />
      </Box>
    ),
  });
}

export interface CheckboxInputProps {
  label: string;
  title: string;
  checked?: boolean;
  toggleFunction: React.Dispatch<React.SetStateAction<boolean>>;
}
export function CheckboxInput(props: CheckboxInputProps) {
  const [checked, setChecked] = useState(props.checked ?? false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    props.toggleFunction(event.target.checked);
  };

  return ContentWrapper({
    title: props.title,
    content: (
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={handleChange}
              sx={{
                color: "#333",
                "&.Mui-checked": {
                  color: "#333",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{
                color: "#2b2b2b",
                fontWeight: 500,
                userSelect: "none",
              }}
            >
              {props.label}
            </Typography>
          }
        />
      </Box>
    ),
  });
}

// TODO add an option to update each ingrient before submitting
export function IngredientInput() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        p: 4,
        borderRadius: 4,
        background: "linear-gradient(135deg, #e0e7ef 0%, #d6dde5 100%)",
        boxShadow: "0 8px 40px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        maxWidth: 700,
        mx: "auto",
      }}
    >
      {[
        "amount",
        "brands",
        "imageUrls",
        "location",
        "name",
        "shoppingUnit",
        "showAsShoppingListItem",
        "unit",
      ].map((i) => {
        return TextInput({ placeholder: i, title: i });
      })}
    </Box>
  );
}
export interface InputField {
  readonly label: string;
  readonly state: string;
  readonly setState: React.Dispatch<React.SetStateAction<string>>;
}

export default function RecipeEditorModal(props: RecipeEditorProps): ReactNode {
  const [hideOptional, setHideOptional] = useState(true);
  const instructionInputs: InstructionInput[] = [
    {
      instruction: "shoppingListIngredients",
      multiline: true,
      placeholder: "Add ingredients that need to be shopped for",
      required: true,
      expandible: false,
      title: "shopping ingredients",
    },
    {
      instruction: "ingredients",
      multiline: true,
      placeholder:
        "Add additional ingredients (e.g. spices you typically have)",
      required: false,
      expandible: true,
      title: "ingredients",
    },
    {
      instruction: "instructions",
      multiline: true,
      placeholder: "add instructions here",
      required: true,
      expandible: false,
      title: "instructions",
    },
    {
      instruction: "cookProcess",
      placeholder: "Add cooking method (e.g. oven, stove, etc.)",
      required: false,
      expandible: true,
      title: "cooking method",
    },
    {
      instruction: "cookTemperature",
      placeholder: "Add temperature this cooked at",
      required: false,
      expandible: true,
      title: "cooking temperature",
    },
    {
      instruction: "cookTime",
      placeholder: "Add time it take to cook",
      required: false,
      expandible: true,
      title: "Cook Time",
    },
    {
      instruction: "notes",
      placeholder: "Add any notes",
      required: false,
      expandible: true,
      title: "notes",
    },
    {
      instruction: "prepTime",
      placeholder: "Add time it takes to prepare",
      required: false,
      expandible: true,
      title: "preperation time",
    },
    {
      instruction: "reminders",
      placeholder: "Add reminders",
      required: false,
      expandible: true,
      title: "reminders",
    },
    {
      instruction: "tips",
      placeholder: "Add tips",
      required: false,
      expandible: true,
      title: "tips",
    },
    {
      instruction: "totalTime",
      placeholder: "Add total time to cook",
      required: false,
      expandible: true,
      title: "Total Time",
    },
  ];
  const [recipeName, setRecipeName] = useState("");
  const [shoppingListIngredients, setShoppingListIngredients] = useState("");
  const [instructions, setInstructions] = useState("");
  const inputFields: InputField[] = [
    {
      label: "recipe name",
      state: recipeName,
      setState: setRecipeName,
    },
    {
      label: "shopping list ingredients",
      state: shoppingListIngredients,
      setState: setShoppingListIngredients,
    },
    {
      label: "instructions",
      state: instructions,
      setState: setInstructions,
    },
  ];

  useEffect(() => {
    setRecipeName(props.state.selectedRecipe?.name ?? "");
    const ingredientsRaw = props.state.selectedRecipe?.instructions.map(
      (instruction) => {
        return instruction.ingredients?.map((ingredient) => {
          return ingredient.showAsShoppingListItem
            ? ingredient.name
            : undefined;
        });
      },
    );
    const ingredients = ingredientsRaw
      ?.flat()
      .filter((item) => item !== undefined)
      .join("\n")
      .toLowerCase();
    setShoppingListIngredients(ingredients ?? "");
    const instructionsRaw = props.state.selectedRecipe?.instructions.map(
      (instructions) => {
        return instructions.instructions;
      },
    );
    const newInstructions = instructionsRaw
      ?.flat()
      .filter((item) => item !== undefined)
      .join("\n")
      .toLowerCase();
    setInstructions(newInstructions ?? "");
  }, [props.state.selectedRecipe]);

  const submit = (setState: React.Dispatch<React.SetStateAction<State>>) => {
    const recipeInstructions: Instruction[] = [
      {
        instructions: instructions.toLowerCase().split("\n"),
        ingredients: shoppingListIngredients
          .toLowerCase()
          .split("\n")
          .map((i) => {
            return {
              name: i,
              showAsShoppingListItem: true,
            };
          }),
      },
    ];
    const recipe: Recipe = {
      instructions: recipeInstructions,
      name: recipeName,
      // author: localStorage.getItem("email") ?? undefined,
    };
    if (props.state.selectedRecipe) {
      updateRecipe({ ...recipe });
    } else {
      writeRecipe({ ...recipe });
    }
    setState((prevState: State) => {
      return {
        ...prevState,
        openModals: {
          ...prevState.openModals,
          newRecipe: false,
        },
        selectedRecipe: undefined,
      };
    });
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "90%",
        height: "90%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        p: 4,
        borderRadius: 4,
        placeItems: "center",
        zIndex: -5, // TO allow x button to close
      }}
    >
      {inputFields.map((inputField: InputField) => {
        return (
          <TextField
            fullWidth
            multiline={inputField.label === "recipe name" ? undefined : true}
            rows={4}
            sx={{ display: "block" }}
            id="outlined-basic"
            label={inputField.label}
            value={inputField.state}
            onChange={(e) => {
              inputField.setState(e.target.value);
            }}
            variant="outlined"
          />
        );
      })}
      <Button
        onClick={() => {
          submit(props.setState);
        }}
        variant="outlined"
      >
        submit
      </Button>
      {SearchBox({
        ...props,
        recipes: JSON.parse(
          localStorage.getItem(localStorageRecipeKey) ?? "{}",
        ),
      })}
    </Box>
  );
}
